import { useCallback } from 'react';
import { getToken } from '@/lib/msalConfig';
import type { ImportProviderConfig } from '@/store/publicUploadApi';

export interface OneDrivePickedItem {
  id: string;
  driveId: string;
  endpoint: string;
  name: string;
  isFolder: boolean;
}

export interface OneDrivePickerResult {
  items: OneDrivePickedItem[];
  accessToken: string;
}

const baseUrl = 'https://onedrive.live.com/picker';

/**
 * OneDrive Picker hook for the public upload page.
 * Accepts config as a parameter instead of fetching from the authenticated API.
 */
export function usePublicOneDrivePicker(config: ImportProviderConfig | null) {
  const openPicker = useCallback(async (): Promise<OneDrivePickerResult | null> => {
    const clientId = config?.microsoft.clientId ?? '';
    if (!clientId) {
      throw new Error('OneDrive picker not configured');
    }

    const authToken = await getToken(clientId);

    const params = {
      sdk: '8.0',
      entry: { oneDrive: { files: {} } },
      authentication: {},
      messaging: {
        origin: window.location.origin,
        channelId: crypto.randomUUID(),
      },
      typesAndSources: {
        mode: 'all',
        pivots: { oneDrive: true, recent: true },
      },
    };

    const win = window.open('', 'OneDrivePicker', 'width=800,height=600');
    if (!win) {
      throw new Error('Could not open picker window');
    }

    const queryString = new URLSearchParams({
      filePicker: JSON.stringify(params),
    });
    const url = `${baseUrl}?${queryString}`;

    const form = win.document.createElement('form');
    form.setAttribute('action', url);
    form.setAttribute('method', 'POST');
    win.document.body.append(form);

    const input = win.document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('name', 'access_token');
    input.setAttribute('value', authToken);
    form.appendChild(input);

    form.submit();

    return new Promise<OneDrivePickerResult | null>((resolve) => {
      let port: MessagePort | null = null;
      let lastToken = authToken;
      let resolved = false;

      const safeResolve = (value: OneDrivePickerResult | null) => {
        if (!resolved) {
          resolved = true;
          resolve(value);
        }
      };

      const cleanup = () => {
        window.removeEventListener('message', onMessage);
        window.removeEventListener('focus', onFocus);
        clearInterval(checkClosed);
        if (port) {
          port.removeEventListener('message', messageListener);
          port.close();
        }
      };

      const onFocus = () => {
        setTimeout(() => {
          try {
            if (win.closed) {
              safeResolve(null);
              cleanup();
            }
          } catch {
            safeResolve(null);
            cleanup();
          }
        }, 500);
      };

      async function messageListener(message: MessageEvent) {
        switch (message.data.type) {
          case 'notification':
            break;

          case 'command': {
            port!.postMessage({ type: 'acknowledge', id: message.data.id });

            const command = message.data.data;

            switch (command.command) {
              case 'authenticate': {
                try {
                  const token = await getToken(clientId);
                  lastToken = token;
                  port!.postMessage({
                    type: 'result',
                    id: message.data.id,
                    data: { result: 'token', token },
                  });
                } catch {
                  // Token acquisition failed silently
                }
                break;
              }

              case 'close': {
                safeResolve(null);
                cleanup();
                win?.close();
                break;
              }

              case 'pick': {
                const rawItems = command.items || [];
                const pickedItems: OneDrivePickedItem[] = rawItems.map(
                  (item: {
                    id: string;
                    name: string;
                    folder?: object;
                    parentReference?: { driveId: string };
                    '@sharePoint.endpoint'?: string;
                  }) => ({
                    id: item.id,
                    driveId: item.parentReference?.driveId ?? '',
                    endpoint: item['@sharePoint.endpoint'] ?? '',
                    name: item.name ?? '',
                    isFolder: !!item.folder,
                  }),
                );
                port!.postMessage({
                  type: 'result',
                  id: message.data.id,
                  data: { result: 'success' },
                });
                safeResolve({
                  items: pickedItems,
                  accessToken: lastToken,
                });
                cleanup();
                win?.close();
                break;
              }

              default:
                port!.postMessage({
                  result: 'error',
                  error: { code: 'unsupportedCommand', message: command.command },
                  isExpected: true,
                });
                break;
            }
            break;
          }
        }
      }

      function onMessage(event: MessageEvent) {
        if (event.source && event.source === win) {
          const message = event.data;

          if (message.type === 'initialize' && message.channelId === params.messaging.channelId) {
            port = event.ports[0];
            port.addEventListener('message', messageListener);
            port.start();
            port.postMessage({ type: 'activate' });
          }
        }
      }

      const checkClosed = setInterval(() => {
        try {
          if (win.closed) {
            safeResolve(null);
            cleanup();
          }
        } catch {
          safeResolve(null);
          cleanup();
        }
      }, 500);

      window.addEventListener('message', onMessage);
      window.addEventListener('focus', onFocus);
    });
  }, [config]);

  return { openPicker, isConfigured: !!config?.microsoft.clientId };
}
